/**
 * Admin Setup Utility
 * Creates admin user and updates Marli's role to professional
 */

import { supabase } from '../services/supabaseService';
import { authService } from '../services/authService';

// Admin password must be set via environment variable or passed as parameter
// NEVER hardcode passwords in production code
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_INITIAL_PASSWORD || '';

export interface SetupResult {
  success: boolean;
  message: string;
  adminUser?: any;
  marliUpdated?: boolean;
}

/**
 * Setup admin user and update Marli's role
 */
export async function setupAdminAndUpdateMarli(customPassword?: string): Promise<SetupResult> {
  const password = customPassword || ADMIN_PASSWORD;

  if (!password || password.length < 8) {
    return {
      success: false,
      message: 'Admin password must be provided via REACT_APP_ADMIN_INITIAL_PASSWORD env var or parameter (min 8 chars)'
    };
  }

  try {
    // 1. Get the organization
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('deleted', false)
      .single();

    if (orgError || !org) {
      return { success: false, message: 'Organization not found' };
    }

    // 2. Get roles
    const { data: roles, error: rolesError } = await supabase
      .from('roles')
      .select('*')
      .eq('organization_id', org.id)
      .eq('deleted', false);

    if (rolesError || !roles) {
      return { success: false, message: 'Roles not found' };
    }

    const adminRole = roles.find(r => r.name === 'admin');
    const professionalRole = roles.find(r => r.name === 'professional');

    if (!adminRole || !professionalRole) {
      return { success: false, message: 'Admin or professional role not found' };
    }

    // 3. Check if admin user already exists
    const { data: existingAdmin } = await supabase
      .from('users')
      .select('*')
      .eq('email', 'admin@psicosys.com')
      .eq('deleted', false)
      .single();

    let adminUser = existingAdmin;

    if (!existingAdmin) {
      // 4. Create admin user
      const { data: newAdmin, error: adminError } = await supabase
        .from('users')
        .insert({
          email: 'admin@psicosys.com',
          full_name: 'Administrador',
          organization_id: org.id,
          role_id: adminRole.id,
          is_active: true,
          invitation_accepted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (adminError) {
        console.error('Error creating admin:', adminError);
        return { success: false, message: `Error creating admin: ${adminError.message}` };
      }

      adminUser = newAdmin;
      console.log('Admin user created:', newAdmin.email);

      // Create local auth credentials for admin
      const authResult = await authService.createUser('admin@psicosys.com', password);
      if (!authResult.success && authResult.error) {
        console.error('Admin local credentials error:', authResult.error);
      }
    } else {
      // Ensure local credentials exist
      await authService.createUser('admin@psicosys.com', password);
    }

    // 5. Update Marli's role to professional
    const { data: marli, error: marliError } = await supabase
      .from('users')
      .select('*')
      .eq('email', 'marli@psicosys.com')
      .eq('deleted', false)
      .single();

    if (marliError || !marli) {
      return {
        success: true,
        message: 'Admin created but Marli user not found to update',
        adminUser
      };
    }

    // Update Marli's role to professional
    const { error: updateError } = await supabase
      .from('users')
      .update({ role_id: professionalRole.id })
      .eq('id', marli.id);

    if (updateError) {
      return {
        success: true,
        message: `Admin created but failed to update Marli: ${updateError.message}`,
        adminUser
      };
    }

    console.log('Marli role updated to professional');

    return {
      success: true,
      message: 'Admin created and Marli updated to professional successfully',
      adminUser,
      marliUpdated: true,
    };
  } catch (error) {
    console.error('Setup error:', error);
    return {
      success: false,
      message: `Setup error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Get current users with their roles
 */
export async function getCurrentUsers(): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        email,
        full_name,
        is_active,
        role:roles(id, name, display_name)
      `)
      .eq('deleted', false)
      .order('full_name');

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getCurrentUsers:', error);
    return [];
  }
}

// `window.adminSetup` removido: expunha admin functions globalmente em produção.
// Se precisar invocar via console, importar em runtime via React DevTools.
